import React, { useState } from 'react';
import type { ConversationAnalyticsData } from '../../../types';
import { useConversationAnalytics } from '../hooks/useConversationAnalytics';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface Props {
  domainId: string;
}

const getDefaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(from), to: fmt(to) };
};

export const ConversationAnalytics: React.FC<Props> = ({ domainId }) => {
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [activeTab, setActiveTab] = useState<'overview' | 'quality'>('overview');

  const { data, isLoading, error } = useConversationAnalytics(domainId, fromDate, toDate);
  const analytics: ConversationAnalyticsData | undefined = data?.data;

  const hasData = analytics && (
    (analytics.summary.chat_users_count ?? 0) > 0 ||
    analytics.daily_trend.length > 0 ||
    analytics.top_questions.length > 0
  );

  return (
    <div>
      {/* 日付フィルタ */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div>
          <label className="block text-xs text-text-secondary mb-1">開始日</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="quality-from-date"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">終了日</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="quality-to-date"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* タブ切替 */}
      <div className="flex gap-1 mb-6 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          概要
        </button>
        <button
          data-testid="quality-tab"
          onClick={() => setActiveTab('quality')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'quality'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          品質
        </button>
      </div>

      {/* ローディング */}
      {isLoading && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-gray-200 rounded-lg h-24" />
            ))}
          </div>
          <div className="animate-pulse bg-gray-200 rounded-lg h-48 mb-4" />
          <div className="animate-pulse bg-gray-200 rounded-lg h-48" />
        </div>
      )}

      {/* データなし */}
      {!isLoading && (!hasData || error) && (
        <div className="text-center py-12" data-testid="analytics-empty-state">
          <p className="text-text-secondary">この期間のデータはありません</p>
        </div>
      )}

      {/* データあり: 概要タブ */}
      {!isLoading && hasData && analytics && activeTab === 'overview' && (
        <div>
          {/* セクションA: サマリーカード */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-text-secondary">チャット利用者数</p>
              <p className="text-2xl font-semibold mt-1" data-testid="analytics-chat-users">
                {(analytics.summary.chat_users_count ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-text-secondary">総メッセージ数</p>
              <p className="text-2xl font-semibold mt-1" data-testid="analytics-total-messages">
                {analytics.summary.total_messages.toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <p className="text-xs text-text-secondary">平均会話長</p>
              <p className="text-2xl font-semibold mt-1" data-testid="analytics-avg-messages">
                {analytics.summary.avg_messages_per_session.toFixed(1)}
              </p>
            </div>
          </div>

          {/* セクションB: 日別推移 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2">日別推移</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-daily-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium">日付</th>
                    <th className="px-4 py-2 text-right font-medium">チャット利用者数</th>
                    <th className="px-4 py-2 text-right font-medium">メッセージ数</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.daily_trend.map((row) => (
                    <tr key={row.date} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">{row.date}</td>
                      <td className="px-4 py-2 text-right">{row.chat_users_count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">{row.message_count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* セクションC: 時間帯分布 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2">時間帯分布</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-hourly-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium">時間帯</th>
                    <th className="px-4 py-2 text-right font-medium">件数</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 24 }, (_, hour) => {
                    const row = analytics.hourly_distribution.find((r) => r.hour === hour);
                    return (
                      <tr key={hour} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">{`${String(hour).padStart(2, '0')}:00`}</td>
                        <td className="px-4 py-2 text-right">{(row?.count ?? 0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* セクションD: よくある質問 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2">よくある質問 TOP10</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-top-questions">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-2 text-left font-medium">順位</th>
                    <th className="px-4 py-2 text-left font-medium">質問</th>
                    <th className="px-4 py-2 text-right font-medium">件数</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_questions.slice(0, 10).map((row, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">{idx + 1}</td>
                      <td className="px-4 py-2">{row.content}</td>
                      <td className="px-4 py-2 text-right">{row.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* セクションE: 満足度 */}
          <div data-testid="analytics-feedback-summary">
            <h3 className="text-sm font-semibold mb-2">満足度サマリー</h3>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-text-secondary">👍 件数</p>
                  <p className="text-xl font-semibold mt-1" data-testid="analytics-positive-count">
                    {analytics.feedback_summary.positive_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">👎 件数</p>
                  <p className="text-xl font-semibold mt-1" data-testid="analytics-negative-count">
                    {analytics.feedback_summary.negative_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">合計件数</p>
                  <p className="text-xl font-semibold mt-1">
                    {analytics.feedback_summary.total_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">肯定率</p>
                  <p className="text-xl font-semibold mt-1" data-testid="analytics-positive-rate">
                    {(analytics.feedback_summary.positive_rate * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* データあり: 品質タブ */}
      {!isLoading && hasData && analytics && activeTab === 'quality' && (
        <div>
          {/* KPIカード3枚 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="positive-rate">
              <p className="text-xs text-text-secondary">肯定率</p>
              <p className="text-2xl font-semibold mt-1">
                {(analytics.feedback_summary.positive_rate * 100).toFixed(1)}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="total-feedback">
              <p className="text-xs text-text-secondary">フィードバック総数</p>
              <p className="text-2xl font-semibold mt-1">
                {analytics.feedback_summary.total_count.toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="fallback-rate">
              <p className="text-xs text-text-secondary">フォールバック率</p>
              <p className="text-2xl font-semibold mt-1">
                {(analytics.quality_metrics.fallback_rate * 100).toFixed(1)}%
              </p>
            </div>
          </div>

          {/* feedback-trend-chart: 日別positive/negative推移 */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6" data-testid="feedback-trend-chart">
            <h3 className="text-sm font-semibold mb-3">フィードバック推移</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.feedback_daily_trend}>
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="positive_count" name="👍" stroke="#4CAF50" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="negative_count" name="👎" stroke="#f44336" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* fallback-rate-chart: PieChart */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6" data-testid="fallback-rate-chart">
            <h3 className="text-sm font-semibold mb-3">フォールバック比率</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    { name: '通常応答', value: 1 - analytics.quality_metrics.fallback_rate },
                    { name: 'フォールバック', value: analytics.quality_metrics.fallback_rate },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  label={({ name, value }: { name: string; value: number }) => `${name}: ${(value * 100).toFixed(1)}%`}
                >
                  <Cell fill="#4CAF50" />
                  <Cell fill="#ff9800" />
                </Pie>
                <Tooltip formatter={(value: number) => `${(value * 100).toFixed(1)}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* low-rated-messages-list: negative一覧テーブル */}
          <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="low-rated-messages-list">
            <h3 className="text-sm font-semibold mb-3">低評価メッセージ一覧</h3>
            {analytics.low_rated_messages.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-4 py-2 text-left font-medium">日時</th>
                      <th className="px-4 py-2 text-left font-medium">内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.low_rated_messages.map((msg) => (
                      <tr key={msg.message_id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap">{new Date(msg.created_at).toLocaleString('ja-JP')}</td>
                        <td className="px-4 py-2">{msg.content}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-text-secondary">低評価メッセージはありません</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
